/**
 * Shared chrome for the admin screen's panes and modals.
 *
 * These class strings used to live as consts at the top of `admin-screen.ts`.
 * They moved here when the screen was split into per-pane modules, so the panes
 * and the modals render the same inputs and buttons instead of each growing its
 * own near-duplicate — which is already what happened once between
 * `admin-screen.ts` and `core/payments/widgets/classes.ts`.
 */

export function escapeHtml(value: unknown): string {
  const div = document.createElement('div');
  div.textContent = String(value ?? '');
  return div.innerHTML;
}

export function money(cents: number, currency = 'EUR'): string {
  return `${(cents / 100).toFixed(2)} ${currency}`;
}

/** Date + time, because an invoice row needs both. '—' for anything unparseable. */
export function when(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export const INPUT_CLS =
  'w-full min-h-11 px-2 bg-surface-2 border border-border rounded text-text text-sm focus:border-gold outline-none';
export const BTN_CLS =
  'font-button min-h-11 px-4 bg-gold text-bg text-sm rounded hover:bg-gold-bright disabled:bg-border disabled:text-text-dim touch-manipulation';
export const SMALL_BTN_CLS =
  'min-h-11 px-2 text-[11px] border border-border rounded text-text-dim hover:text-text hover:border-text-dim transition-colors duration-[120ms] touch-manipulation';

/**
 * One headline number. Ported from steel-of-war-core's `.tile` idiom
 * (frontend/src/admin/pane.css there) as Tailwind utilities rather than a new
 * stylesheet — the number is the point, so it is set large and
 * `tabular-nums` so a column of them does not jitter as values change.
 */
export function statTile(label: string, value: string, flagged = false): string {
  return `
    <div class="px-3 py-2.5 border rounded ${flagged ? 'border-gold' : 'border-border'} bg-surface-2">
      <div class="text-2xl tabular-nums leading-tight ${flagged ? 'text-gold' : 'text-text'}">${escapeHtml(value)}</div>
      <div class="mt-0.5 text-[0.7rem] uppercase tracking-wide text-text-dim">${escapeHtml(label)}</div>
    </div>`;
}

export function tileGrid(tiles: string): string {
  return `<div class="grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-2.5">${tiles}</div>`;
}

/**
 * A labelled on/off switch. Markup matches the player-facing toggles in
 * `screens/settings/settings-screen.ts` so the two screens do not drift.
 */
export function toggleSwitch(action: string, label: string, hint: string, on: boolean, busy = false): string {
  return `
    <div class="flex items-center justify-between gap-3 py-2">
      <div class="min-w-0">
        <div class="text-sm text-text">${escapeHtml(label)}</div>
        <div class="text-[10px] text-text-dim leading-relaxed">${escapeHtml(hint)}</div>
      </div>
      <button type="button" role="switch" aria-checked="${on}" aria-label="${escapeHtml(label)}"
        data-action="${action}" ${busy ? 'disabled' : ''}
        class="shrink-0 w-11 h-6 rounded-full border transition-colors duration-[120ms] touch-manipulation ${
          on ? 'bg-gold border-gold' : 'bg-surface-2 border-border'
        }">
        <span class="block w-5 h-5 rounded-full bg-bg transition-transform duration-[120ms] ${on ? 'translate-x-5' : 'translate-x-0.5'}"></span>
      </button>
    </div>`;
}
